import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThemeContext } from "../ThemeContext";
import { CognitoUserAttribute, CognitoUser } from 'amazon-cognito-identity-js';
import UserPool from '../UserPool';
import { Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../api';
import { startGoogleSignIn } from '../utils/cognitoOAuth';
import GoogleLogo from '../components/GoogleLogo';
import {
  refreshIdentityPlatformUser,
  signInWithGoogleIdentityPlatform,
  signOutIdentityPlatform,
  signUpWithIdentityPlatform,
  usesIdentityPlatform,
} from '../identityPlatform';


// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

const SignupPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const termsAcceptanceRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [screenName, setScreenName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [cognitoUser, setCognitoUser] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  // Validation and UI states
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  //show password states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(null);

  // Handle referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
      // Store in localStorage for later use
      localStorage.setItem('pending_referral_code', ref);
    }
  }, [searchParams]);

  //able to change email states
  const [allowEdit, setAllowEdit] = useState(false);



  // Password regex for validation
 const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#-])[A-Za-z\d@$!%*?&#-]{8,}$/;

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (pass) => {
    return passwordRegex.test(pass);
  };

  // Check if all required fields are filled
  const areFieldsFilled = () => {
    return (
      firstName.trim() &&
      lastName.trim() &&
      screenName.trim() &&
      email.trim() &&
      password &&
      confirmPassword
    );
  };

  const handleGetCode = async () => {
    if (!validateEmail(email)) {
      setFormErrors({ ...formErrors, email: "Please enter a valid email address" });
      return;
    }

    if (!areFieldsFilled()) {
      setFormErrors({ ...formErrors, general: "Please fill in all required fields" });
      return;
    }

    if (!termsAccepted) {
      setFormErrors({ ...formErrors, general: "Please accept the Terms of Service and Privacy Policy to continue" });
      return;
    }

    try {
      setIsSubmitting(true);
      if (usesIdentityPlatform) {
        const identityUser = await signUpWithIdentityPlatform({
          email,
          password,
          firstName,
          lastName,
          screenName,
        });
        setCognitoUser(identityUser);
        setIsCodeSent(true);
        setFormErrors({});
        setStatusMessage(
          'Verification link sent. Open it, then return here to finish signing up.'
        );
        setMessageType('success');
        setIsSubmitting(false);
        return;
      }

      const userAttributes = [
        new CognitoUserAttribute({ Name: 'custom:FirstName', Value: firstName }),
        new CognitoUserAttribute({ Name: 'custom:LastName', Value: lastName }),
        new CognitoUserAttribute({ Name: 'custom:ScreenName', Value: screenName }),
      ];

      UserPool.signUp(email, password, userAttributes, null, (err, data) => {
        if (err) {
          console.error('Sign up error:', err);
          if (err.code === "UsernameExistsException") {
            setStatusMessage("An account with this email already exists. Please log in with your credentials.");
            setMessageType("error");
          } else {
            setStatusMessage('Failed to send verification code');
            setMessageType('error');
          }
         // setFormErrors({ ...formErrors, general: err.message });
          setIsSubmitting(false);
          return;
        } else {
          setIsCodeSent(true);
          setFormErrors({});
          // Store the Cognito user for verification
          const userData = {
            Username: email,
            Pool: UserPool
          };
          setCognitoUser(new CognitoUser(userData));
          setStatusMessage('Verification code sent to your email');
          setMessageType('success');
        }
        setIsSubmitting(false);
      });
    } catch (error) {
      if (usesIdentityPlatform) {
        await signOutIdentityPlatform().catch(() => {});
      }
      setFormErrors({ ...formErrors, general: error.message });
      setStatusMessage(
        error.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists. Please log in.'
          : 'Failed to create the account or send its verification message.'
      );
      setMessageType('error');
      setIsSubmitting(false);
    }
  };

  const handleReferralCode = async () => {
    if (!referralCode) {
      setStatusMessage('Email verified successfully! Redirecting to login...');
      setMessageType('success');
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    else{
      const storedReferralCode = localStorage.getItem('pending_referral_code');
      if (!storedReferralCode || !email) {
        return; // No referral code or email, skip
      }

      try {  
          // Call mark_referred API
          api.post('/api/refer/mark_referred/', {
            referral_code: storedReferralCode,
            email: email
          }).then(response => {
            console.log('Referral marked successfully:', response.data);
            // Clear the stored referral code
            localStorage.removeItem('pending_referral_code');
            setStatusMessage('Email verified successfully! Redirecting to login...');
            setMessageType('success');
            setTimeout(() => navigate("/login"), 2000);
          }).catch(error => {
            console.error('Error marking referral:', error);
            // Don't block the user flow if referral marking fails
          });
      } catch (error) {
        console.error('Error handling referral code:', error);
        // Don't block the user flow if referral marking fails
      }
    }
  };

  const verifyAndFinalize = async () => {
    if (!usesIdentityPlatform && !verificationCode) {
      setFormErrors({ ...formErrors, code: "Please enter verification code" });
      return;
    }

    if (!cognitoUser && !usesIdentityPlatform) {
      setFormErrors({ ...formErrors, general: "Something went wrong. Please try again." });
      return;
    }

    try {
      setIsSubmitting(true);
      if (usesIdentityPlatform) {
        const identityUser = await refreshIdentityPlatformUser();
        if (!identityUser?.emailVerified) {
          setStatusMessage(
            'Email is not verified yet. Open the verification link, then try again.'
          );
          setMessageType('error');
          setIsSubmitting(false);
          return;
        }

        const idToken = await identityUser.getIdToken(true);
        localStorage.setItem('idToken', idToken);
        await api.post(
          '/api/user/sync_oauth_user/',
          {
            allow_create: true,
            first_name: firstName,
            last_name: lastName,
            screen_name: screenName,
          },
          { headers: { Authorization: `Bearer ${idToken}` } },
        );

        const storedReferralCode = localStorage.getItem('pending_referral_code');
        if (storedReferralCode) {
          try {
            await api.post('/api/refer/mark_referred/', {
              referral_code: storedReferralCode,
              email,
            });
            localStorage.removeItem('pending_referral_code');
          } catch (error) {
            console.error('Error marking referral:', error);
          }
        }

        await signOutIdentityPlatform();
        localStorage.removeItem('idToken');
        setFormErrors({});
        setStatusMessage('Email verified successfully! Redirecting to login...');
        setMessageType('success');
        setTimeout(() => navigate('/login'), 1500);
        return;
      }

      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          console.error('Verification error:', err);
          if (err.message && err.message.includes("UserLambdaValidationException") &&
            err.message.includes("Can't connect to MySQL server")) {
            console.log('Email verified but database connection failed - proceeding anyway');
            setFormErrors({});
            setStatusMessage('Email verified successfully! Redirecting to login...');
            setMessageType('success');
            setTimeout(() => navigate("/login"), 2000);
          } else {
            setFormErrors({ ...formErrors, code: err.message || "Failed to verify code" });
            setStatusMessage('Invalid verification code');
            setMessageType('error');
          }
          setIsSubmitting(false);
          return;
        }
        console.log('Confirmation result:', result);
        setFormErrors({});

        handleReferralCode();
      });
    } catch (error) {
      setFormErrors({ ...formErrors, code: error.message });
      setStatusMessage('Failed to verify code');
      setMessageType('error');
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!isCodeSent) {
      handleGetCode();
    } else {
      verifyAndFinalize();
    }
  };

  const handleGoogleSignIn = async () => {
    if (!termsAccepted) {
      setStatusMessage('Please accept the Terms of Service and Privacy Policy to continue with Google.');
      setMessageType('error');
      termsAcceptanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (usesIdentityPlatform) {
      try {
        setIsGoogleSubmitting(true);
        setStatusMessage('');
        setMessageType('');
        const credential = await signInWithGoogleIdentityPlatform();
        const displayName = credential.user.displayName || '';
        const [googleFirstName = '', ...remainingNames] = displayName.trim().split(/\s+/);
        const idToken = await credential.user.getIdToken(true);
        localStorage.setItem('idToken', idToken);
        await api.post(
          '/api/user/sync_oauth_user/',
          {
            allow_create: true,
            first_name: googleFirstName,
            last_name: remainingNames.join(' '),
            screen_name: displayName || credential.user.email?.split('@')[0] || 'student',
          },
          { headers: { Authorization: `Bearer ${idToken}` } },
        );
        await signOutIdentityPlatform();
        localStorage.removeItem('idToken');
        setStatusMessage('Account created successfully. Redirecting to login...');
        setMessageType('success');
        setTimeout(() => navigate('/login'), 1000);
      } catch (error) {
        await signOutIdentityPlatform().catch(() => {});
        localStorage.removeItem('idToken');
        setStatusMessage(
          error.response?.data?.error ||
          error.message ||
          'Google sign-up could not be completed.'
        );
        setMessageType('error');
        setIsGoogleSubmitting(false);
      }
      return;
    }

    try {
      setIsGoogleSubmitting(true);
      setStatusMessage('');
      setMessageType('');
      await startGoogleSignIn({ allowCreate: true });
    } catch (error) {
      setStatusMessage(error.message || 'Google sign-in could not be started.');
      setMessageType('error');
      setIsGoogleSubmitting(false);
    }
  };

  // handle 
  const handleChangeEmail = () => {
    setIsCodeSent(false);
    setAllowEdit(false);
    setCognitoUser(null);
    setVerificationCode('');
    setStatusMessage('');
    setMessageType('');
    setFormErrors({});
    setIsSubmitting(false);
  };


  // Update password validation on change
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setIsPasswordValid(validatePassword(newPassword));
    setPasswordsMatch(newPassword === confirmPassword);
  };

  // Update confirm password validation on change
  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value;
    setConfirmPassword(confirmPass);
    setPasswordsMatch(password === confirmPass);
  };

  const isButtonDisabled = () => {
    if (isSubmitting) return true;
    if (!isCodeSent) {
      return !areFieldsFilled() || !termsAccepted || !isEmailValid || !isPasswordValid || !passwordsMatch;
    } else {
      if (usesIdentityPlatform) return false;
      return !verificationCode;
    }
  };

  const getDisabledHelpText = () => {
    if (isSubmitting) return '';
    if (!isCodeSent) {
      if (!areFieldsFilled()) return 'Fill in the fields above to continue.';
      if (!termsAccepted) return 'Accept the Terms of Service and Privacy Policy to continue.';
      if (!isEmailValid) return 'Enter a valid email address to continue.';
      if (!isPasswordValid) return 'Use a password that meets the requirements to continue.';
      if (!passwordsMatch) return 'Make sure both passwords match to continue.';
      return '';
    }

    if (usesIdentityPlatform) return 'Open the verification link, then return here.';
    if (!verificationCode) return 'Enter the verification code to continue.';
    return '';
  };

  const getButtonText = () => {
    if (!isCodeSent) {
      return "SIGN UP";
    } else {
      if (usesIdentityPlatform) return "I'VE VERIFIED MY EMAIL";
      return "VERIFY & SIGN UP";
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center font-zonaProRegular p-4 pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="w-full max-w-2xl space-y-6 sm:space-y-8">
        <Card delay={0.3} className="p-4 mt-28 sm:p-6 md:p-8">
          <motion.h1 
            className="text-2xl sm:text-3xl md:text-4xl font-bold font-zonaProUltra mb-4 sm:mb-6 text-center"
            style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            SIGN UP
          </motion.h1>
          <motion.p
            className="text-center text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
          >
            No credit card required for your 7-day trial.
          </motion.p>

          <motion.button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleSubmitting}
            className={`w-full flex items-center justify-center gap-3 rounded-full border py-3 px-5 text-base sm:text-lg font-zonaProUltra transition-all ${
              isGoogleSubmitting
                ? 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'border-gray-300 dark:border-gray-600 !bg-white dark:!bg-gray-900 text-gray-800 dark:text-gray-100 hover:!bg-gray-50 dark:hover:!bg-gray-800 hover:shadow-md'
            }`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={!isGoogleSubmitting ? { scale: 1.02 } : {}}
            whileTap={!isGoogleSubmitting ? { scale: 0.98 } : {}}
          >
            <GoogleLogo className="h-5 w-5 shrink-0" />
            {isGoogleSubmitting ? 'Opening Google...' : 'Continue with Google'}
          </motion.button>

          <div className="flex items-center gap-3 my-5 text-xs sm:text-sm text-gray-400 dark:text-gray-500">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            <span>OR SIGN UP WITH EMAIL</span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          </div>

          <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                disabled={isCodeSent && !allowEdit}
                required
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.55 }}
            >
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                disabled={isCodeSent && !allowEdit}
                required
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <input
                type="text"
                placeholder="Screen Name"
                value={screenName}
                onChange={(event) => setScreenName(event.target.value)}
                className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                disabled={isCodeSent && !allowEdit}
                required
              />
            </motion.div>
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.65 }}
            >
              <div className="flex">
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setIsEmailValid(validateEmail(e.target.value));
                  }}
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                  disabled={isCodeSent && !allowEdit}
                  required
                />
              </div>
            </motion.div>
            <div className="flex justify-end">
              {isCodeSent && !allowEdit && !usesIdentityPlatform && (
                <motion.p
                  className="mt-1 text-xs sm:text-sm text-zonaProRegular text-blue-600 dark:text-blue-400 hover:underline cursor-pointer transition-colors"
                  onClick={handleChangeEmail}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  Not your email? <span className="font-semibold">Change it</span>
                </motion.p>
              )}
            </div>

          {/* <div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              className="w-full p-4 text-xl font-zonaProRegular border-b border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500"
              disabled={isCodeSent}
              required
            />
            {password && !isPasswordValid && (
              <p className="text-red-500 text-sm mt-1">
                Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character
              </p>
            )}
          </div> */}
            <motion.div 
              className="mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={handlePasswordChange}
                  className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                  disabled={isCodeSent && !allowEdit}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 sm:right-3 top-[50%] -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent focus:outline-none focus:ring-0"
                >
                  {showPassword ? (
                    <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                  )}
                </button>
              </div>
              {password && !isPasswordValid && (
                <p className="mt-2 text-red-500 dark:text-red-400 text-xs sm:text-sm">
                  Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character
                </p>
              )}
            </motion.div>
          {/* <div>
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              className="w-full p-4 text-xl font-zonaProRegular border-b border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500"
              disabled={isCodeSent}
              required
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
            )}
          </div> */}
            <motion.div 
              className="mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.75 }}
            >
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className="w-full p-3 sm:p-4 pr-10 sm:pr-12 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                  disabled={isCodeSent && !allowEdit}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 sm:right-3 top-[50%] -translate-y-1/2 bg-transparent p-0 m-0 border-none hover:bg-transparent focus:outline-none focus:ring-0"
                >
                  {showConfirmPassword ? (
                    <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                  ) : (
                    <EyeOff className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                  )}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="mt-2 text-red-500 dark:text-red-400 text-xs sm:text-sm">Passwords do not match</p>
              )}
            </motion.div>

            {isCodeSent && !usesIdentityPlatform && (
              <motion.div 
                className="flex space-x-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <input
                  type="text"
                  placeholder="Enter verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="w-full p-3 sm:p-4 text-base sm:text-lg md:text-xl font-zonaProRegular border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-green-500 focus:border-green-500 bg-transparent dark:text-gray-100 text-gray-900"
                />
              </motion.div>
            )}
            {statusMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-2 text-xs sm:text-sm p-3 rounded-xl ${messageType === 'success' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300'}`}
              >
                {statusMessage}
              </motion.div>
            )}
            {formErrors.general && (
              <motion.p 
                className="text-red-500 dark:text-red-400 text-xs sm:text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {formErrors.general}
              </motion.p>
            )}
            <motion.div
              ref={termsAcceptanceRef}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40 p-3 sm:p-4"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85 }}
            >
              <label className="flex items-start gap-3 text-left text-sm sm:text-base text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                  disabled={isCodeSent && !allowEdit}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#34c759] focus:ring-[#34c759]"
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="font-medium hover:underline" style={{ color: '#06bae4' }}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="font-medium hover:underline" style={{ color: '#06bae4' }}>
                    Privacy Policy
                  </Link>
                  . I confirm I am 13 or older, or I have parent or guardian consent.
                </span>
              </label>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-left pl-7">
                Students under 13 need a parent or guardian to review and consent before creating an account.
              </p>
            </motion.div>
            <motion.div 
              className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-0 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center space-x-2">
                <h5 className="text-base sm:text-lg font-zonaProRegular text-gray-700 dark:text-gray-300">Already have an account?</h5>
                <Link to="/login" className="font-medium dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-base sm:text-lg font-zonaProRegular transition-colors" style={{ color: '#06bae4' }}>
                  Login
                </Link>
              </div>
              <motion.button
                type="submit"
                disabled={isButtonDisabled()}
                className={`w-full sm:w-auto py-2 sm:py-3 px-6 sm:px-14 ${isButtonDisabled()
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-[#34c759] hover:shadow-lg hover:shadow-[#34c759]/50'
                  } text-white text-base sm:text-lg md:text-xl font-zonaProUltra rounded-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition-all`}
                whileHover={!isButtonDisabled() ? { scale: 1.05 } : {}}
                whileTap={!isButtonDisabled() ? { scale: 0.95 } : {}}
              >
                {getButtonText()}
              </motion.button>
            </motion.div>
            {getDisabledHelpText() && (
              <motion.p
                className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-right"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {getDisabledHelpText()}
              </motion.p>
            )}
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
